<template>
    <!-- Shared shell for the Active groups detail modals (Weight/Water/Mortality): the teleported
         overlay + dialog frame around the shared SectionDetailHeader (section name, meta line, alarm
         chip, prev/next/close nav, Escape/Arrow-key handling). Callers supply only their own headline
         cards and charts via the default slot (scoped with the non-null `section`, since the slot only
         renders while a section is open). -->
    <Teleport to="body">
        <FadeTransition>
            <div
                v-if="section"
                class="fixed inset-0 z-[1030] flex items-center justify-center bg-black/40 p-4"
                @click.self="emit('close')"
            >
                <div
                    class="relative flex max-h-full w-full max-w-2xl flex-col overflow-y-auto rounded-xl bg-dark-gray-200 shadow-xl"
                    role="dialog"
                    aria-modal="true"
                >
                    <SectionDetailHeader
                        :section="section"
                        :sections="sections"
                        @close="emit('close')"
                        @navigate="emit('navigate', $event)"
                        @handle-alarm="emit('handleAlarm', $event)"
                    />

                    <div class="flex flex-col gap-3 px-6 pb-4">
                        <slot :section="section"></slot>
                    </div>
                </div>
            </div>
        </FadeTransition>
    </Teleport>
</template>

<script setup lang="ts">
import FadeTransition from '@/components/transitions/FadeTransition.vue';
import SectionDetailHeader from './SectionDetailHeader.vue';
import type { SectionRow } from './sectionRow';

defineProps<{
    // The row whose cell was clicked (null = closed).
    section: SectionRow | null;
    // All rows whose cells can open this modal, in the page's display order — drives prev/next
    // navigation. The parent stays in control: navigation only emits the target row.
    sections: SectionRow[];
}>();

const emit = defineEmits<{
    close: [];
    navigate: [section: SectionRow];
    handleAlarm: [section: SectionRow];
}>();

defineSlots<{
    default(props: { section: SectionRow }): unknown;
}>();
</script>
